/**
 * API service for communicating with Cloudflare Worker backend
 */

const REQUEST_TIMEOUT_MS = 15000;

async function request(path, { method = 'GET', body } = {}, errorContext = 'Request failed') {

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const REACT_APP_API_URL = "https://humor-leaderboard.gamewith6ot.workers.dev"
  try {
    const response = await fetch(`${REACT_APP_API_URL}${path}`, {
      method,
      body,
      signal: controller.signal,
    });

    const raw = await response.text();
    let payload = null;
    if (raw) {
      try {
        payload = JSON.parse(raw);
      } catch (err) {
        throw new Error(`${errorContext}: Received invalid JSON from server.`);
      }
    }

    if (!response.ok) {
      throw new Error(
        payload?.error?.message || `${errorContext}: ${response.status} ${response.statusText}`,
      );
    }

    if (!payload?.ok) {
      throw new Error(payload?.error?.message || errorContext);
    }

    return payload.data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error(`${errorContext}: Request timed out after ${REQUEST_TIMEOUT_MS}ms.`);
    }
    if (error instanceof Error && error.message.startsWith(errorContext)) {
      throw error;
    }
    throw new Error(`${errorContext}: ${error.message}`);
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Start a new round — get a question.
 * Calls the unified POST /answer with no choiceId (start-round mode).
 *
 * @param {string|null} gameStateToken - Opaque signed token carrying game state (null = fresh game)
 * @param {boolean}  [skip=false]       - Whether this is a skip action
 * @returns {Promise<{ round, questionId, question, options, corpus, gameStateToken }>}
 */
export async function startRound(gameStateToken, skip = false) {
  return request(
    '/answer',
    {
      method: 'POST',
      body: JSON.stringify({
        gameStateToken: gameStateToken || null,
        skip: skip || undefined,
      }),
    },
    'Failed to start round',
  );
}

/**
 * Submit an answer for a question.
 * Calls the unified POST /answer with choiceId (submit-answer mode).
 *
 * @param {{ questionId: number, choiceId: number, gameStateToken: string }} opts
 * @returns {Promise<{ round, choiceId, choice, isCorrect, reason, questionId, question, options, corpus, gameStateToken }>}
 */
export async function submitAnswer({ questionId, choiceId, gameStateToken }) {
  if (typeof questionId !== 'number' || !Number.isInteger(questionId) || questionId < 0) {
    throw new Error('Invalid questionId');
  }

  const sanitizedChoiceId = Number(choiceId);
  if (!Number.isInteger(sanitizedChoiceId) || sanitizedChoiceId < 0) {
    throw new Error('Invalid choiceId');
  }

  if (!gameStateToken || typeof gameStateToken !== 'string') {
    throw new Error('Invalid gameStateToken');
  }

  return request(
    '/answer',
    {
      method: 'POST',
      body: JSON.stringify({
        questionId,
        choiceId: sanitizedChoiceId,
        gameStateToken,
      }),
    },
    'Failed to submit answer',
  );
}

/**
 * Fetch leaderboard from the backend.
 *
 * Score metric is hardcoded on the backend — the frontend no longer selects
 * which column to fetch. The backend returns scores under a generic `score`
 * key plus `metric` for display labels.
 *
 * @param {string[]}[modelNames] - Specific model names to show; omit for all-provider view
 * @returns {Promise<{ updatedAt: string, metric: string, models: Array }>}
 */
export async function fetchLeaderboard(modelNames = []) {
  const params = new URLSearchParams();
  if (modelNames.length > 0) {
    params.set('models', modelNames.join(','));
  }

  const queryString = params.toString();
  const url = queryString ? `/leaderboard?${queryString}` : '/leaderboard';
  return request(url, {}, 'Failed to load leaderboard');
}
